if (!customElements.get('featured-projects')) {
  customElements.define(
    'featured-projects',
    class FeaturedProjects extends HTMLElement {
      constructor() {
        super();

        this.currentPage = 0;
        this.isAnimating = false;
        this.timeouts = [];
        this.handlePreviousClick = this.handlePreviousClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleLastClick = this.handleLastClick.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;

        window.requestAnimationFrame(() => {
          this.init();
        });
      }

      init() {
        if (this.initialized) return;

        this.grid = this.querySelector('[data-featured-projects-grid]');
        this.items = this.grid ? Array.from(this.grid.querySelectorAll('[data-featured-projects-item]')) : [];
        this.pagination = this.querySelector('[data-featured-projects-pagination]');
        this.pageLabel = this.querySelector('[data-featured-projects-page-label]');
        this.previousButton = this.querySelector('[data-featured-projects-prev]');
        this.nextButton = this.querySelector('[data-featured-projects-next]');
        this.lastButton = this.querySelector('[data-featured-projects-last]');
        this.pageSize = this.grid ? parseInt(this.grid.dataset.pageSize, 10) : 0;
        this.totalPages = this.pageSize ? Math.ceil(this.items.length / this.pageSize) : 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.animationDuration = this.reduceMotion ? 0 : 260;
        this.staggerDuration = this.reduceMotion ? 0 : 30;
        this.enterOverlapDelay = this.reduceMotion ? 0 : 90;

        if (!this.grid || !this.items.length || !this.pageSize || this.pageSize >= this.items.length) {
          if (this.pagination) this.pagination.hidden = true;
          return;
        }

        this.previousButton?.addEventListener('click', this.handlePreviousClick);
        this.nextButton?.addEventListener('click', this.handleNextClick);
        this.lastButton?.addEventListener('click', this.handleLastClick);
        this.initialized = true;
        this.renderPage(0);
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener('click', this.handlePreviousClick);
        this.nextButton?.removeEventListener('click', this.handleNextClick);
        this.lastButton?.removeEventListener('click', this.handleLastClick);
        this.timeouts.forEach((timeout) => window.clearTimeout(timeout));
      }

      handlePreviousClick() {
        this.animateToPage(this.currentPage - 1);
      }

      handleNextClick() {
        this.animateToPage(this.currentPage + 1);
      }

      handleLastClick() {
        this.animateToPage(this.totalPages - 1);
      }

      getPageItems(page) {
        const start = page * this.pageSize;
        return this.items.slice(start, start + this.pageSize);
      }

      updateControls() {
        if (this.pageLabel) this.pageLabel.textContent = `PAGE ${this.currentPage + 1} of ${this.totalPages}`;
        if (this.previousButton) this.previousButton.disabled = this.isAnimating || this.currentPage === 0;
        if (this.nextButton) this.nextButton.disabled = this.isAnimating || this.currentPage === this.totalPages - 1;
        if (this.lastButton) this.lastButton.disabled = this.isAnimating || this.currentPage === this.totalPages - 1;
      }

      setDelay(item, index) {
        item.style.transitionDelay = `${index * this.staggerDuration}ms`;
      }

      clearItemState(item) {
        item.classList.remove(
          'is-leaving-next',
          'is-leaving-previous',
          'is-entering-next',
          'is-entering-previous'
        );
        item.style.transitionDelay = '';
      }

      renderPage(page) {
        const pageItems = this.getPageItems(page);

        this.items.forEach((item) => {
          this.clearItemState(item);
          item.hidden = !pageItems.includes(item);
        });

        this.currentPage = page;
        this.updateControls();
      }

      animateToPage(page) {
        if (this.isAnimating || page === this.currentPage || page < 0 || page >= this.totalPages) return;

        const direction = page > this.currentPage ? 'next' : 'previous';
        const currentItems = this.getPageItems(this.currentPage);
        const nextItems = this.getPageItems(page);
        const leaveDelay = Math.max(currentItems.length - 1, 0) * this.staggerDuration;
        const enterDelay = Math.max(nextItems.length - 1, 0) * this.staggerDuration;
        const leaveDuration = this.animationDuration + leaveDelay;
        const enterDuration = this.animationDuration + enterDelay;

        this.isAnimating = true;
        this.updateControls();

        currentItems.forEach((item, index) => {
          this.setDelay(item, index);
          item.classList.add(`is-leaving-${direction}`);
        });

        this.setTrackedTimeout(() => {
          nextItems.forEach((item, index) => {
            item.hidden = false;
            this.setDelay(item, index);
            item.classList.add(`is-entering-${direction}`);
          });

          this.grid.offsetHeight;

          window.requestAnimationFrame(() => {
            nextItems.forEach((item) => {
              item.classList.remove(`is-entering-${direction}`);
            });
          });

          this.currentPage = page;
          this.updateControls();

          this.setTrackedTimeout(() => {
            nextItems.forEach((item) => this.clearItemState(item));
            this.isAnimating = false;
            this.updateControls();
          }, enterDuration);
        }, this.enterOverlapDelay);

        this.setTrackedTimeout(() => {
          currentItems.forEach((item) => {
            this.clearItemState(item);
            item.hidden = true;
          });
        }, leaveDuration);
      }

      setTrackedTimeout(callback, delay) {
        const timeout = window.setTimeout(() => {
          this.timeouts = this.timeouts.filter((storedTimeout) => storedTimeout !== timeout);
          callback();
        }, delay);

        this.timeouts.push(timeout);
      }
    }
  );
}
